import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";
import { Container, btn } from "@/components/ui-kit";
import { Field, TextInput, TextArea, RadioPills } from "@/components/forms";
import FileUpload from "@/components/FileUpload";
import AntiSpam from "@/components/AntiSpam";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/content";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ArrowLeft, House, ShieldCheck } from "@phosphor-icons/react";

const STEPS = ["Contact", "Property", "Characteristics", "Details", "Review"];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const ROLES = ["Owner", "Broker / Realtor", "Representative", "Other"];
const PROPERTY_TYPES = ["Vacant Land", "Farm", "Residential Property", "Commercial Property", "Other"];

export default function SubmitProperty() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [spam, setSpam] = useState({});
  const [form, setForm] = useState({
    owner_name: "", owner_email: "", owner_phone: "", submitter_role: "",
    property_address: "", city: "", county: "", state: "NC", parcel_id: "",
    acreage: "", current_zoning: "", asking_price: "",
    currently_listed: "", water_available: "", sewer_available: "",
    road_frontage: "", existing_structures: "", property_type: "", additional_info: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setVal = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s = step) => {
    const e = {};
    if (s === 0) {
      if (!form.owner_name.trim()) e.owner_name = "Name is required.";
      if (!emailOk(form.owner_email)) e.owner_email = "A valid email is required.";
      if (!form.owner_phone.trim()) e.owner_phone = "Phone is required.";
      if (!form.submitter_role) e.submitter_role = "Please select one.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep(0)) { setStep(0); return; }
    if (!spam.captcha_answer || spam.captcha_answer !== spam.captcha_a + spam.captcha_b) {
      toast.error("Please answer the verification question correctly.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions/property", { ...form, files, ...spam });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="submit-property-page">
        <Seo title="Submit a Property" description="Thank you for submitting your property to Redwood Capital Holdings." path="/submit-property" />
        <Container className="py-28 md:py-36">
          <div className="max-w-xl mx-auto text-center" data-testid="submit-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center">
              <CheckCircle size={40} weight="light" className="text-gold" />
            </div>
            <h1 className="mt-8 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">Thank You</h1>
            <p className="mt-4 text-ink text-lg leading-relaxed">
              We've received your property information. Our team will review the opportunity and contact you if
              additional information is needed.
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link to="/" className={btn.forest} data-testid="success-home-btn"><House size={18} /> Back to Home</Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const Summary = () => {
    const rows = [
      ["Name", form.owner_name], ["Email", form.owner_email], ["Phone", form.owner_phone],
      ["Submitting as", form.submitter_role], ["Address", form.property_address],
      ["City", form.city], ["County", form.county], ["State", form.state],
      ["Acreage", form.acreage], ["Zoning", form.current_zoning], ["Asking Price", form.asking_price],
      ["Property Type", form.property_type], ["Currently Listed", form.currently_listed],
      ["Public Water", form.water_available], ["Public Sewer", form.sewer_available],
      ["Existing Structures", form.existing_structures],
    ].filter(([, v]) => v);
    return (
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 border border-line rounded-sm p-6 bg-cream">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 text-sm border-b border-line/60 pb-2">
            <dt className="text-ink">{k}</dt>
            <dd className="text-forest font-medium text-right">{v}</dd>
          </div>
        ))}
        {files.length > 0 && (
          <div className="flex justify-between gap-4 text-sm sm:col-span-2 pt-1">
            <dt className="text-ink">Documents attached</dt>
            <dd className="text-forest font-medium">{files.length}</dd>
          </div>
        )}
      </dl>
    );
  };

  return (
    <div data-testid="submit-property-page">
      <Seo
        title="Submit a Property — Sell Your Land in Charlotte, NC"
        description="Submit land or property for review by Redwood Capital Holdings. Whether listed, off-market, inherited, agricultural, or underutilized, we evaluate opportunities individually. Submitting does not obligate you to sell."
        path="/submit-property"
      />

      <section className="bg-forest text-white pt-16 pb-24 relative overflow-hidden">
        <div className="grain" />
        <Container className="relative">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Submit a Property</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-semibold tracking-tight max-w-3xl leading-tight">
            Tell us about your property
          </h1>
          <p className="mt-5 text-white/70 text-lg max-w-2xl leading-relaxed">
            A few quick steps is all it takes. Whether your property is listed, off-market, inherited, agricultural,
            or underutilized, we'd like to learn more.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-white/60 text-sm">
            <ShieldCheck size={18} className="text-gold" />
            Submitting a property does not obligate you to sell.
          </p>
        </Container>
      </section>

      <section className="pb-24 -mt-14">
        <Container>
          <div className="max-w-3xl mx-auto bg-white border border-line rounded-sm shadow-[0_12px_40px_rgba(13,46,27,0.08)] p-6 sm:p-10">
            {/* Stepper */}
            <div className="flex items-center justify-between mb-10" data-testid="form-stepper">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border transition-colors ${
                      i < step ? "bg-gold border-gold text-forest" : i === step ? "bg-forest border-forest text-white" : "bg-white border-line text-ink/50"
                    }`}>
                      {i < step ? <CheckCircle size={18} weight="fill" /> : i + 1}
                    </div>
                    <span className={`mt-2 text-[11px] font-medium uppercase tracking-wider hidden sm:block ${i === step ? "text-forest" : "text-ink/60"}`}>{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-1 ${i < step ? "bg-gold" : "bg-line"}`} />}
                </React.Fragment>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {step === 0 && (
                  <div className="space-y-6" data-testid="step-contact">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-forest">Contact Information</h2>
                      <p className="text-ink text-sm mt-1">How can our team reach you?</p>
                    </div>
                    <Field label="Name" required error={errors.owner_name}>
                      <TextInput value={form.owner_name} onChange={set("owner_name")} error={errors.owner_name} placeholder="Full name" data-testid="input-owner-name" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Email" required error={errors.owner_email}>
                        <TextInput type="email" value={form.owner_email} onChange={set("owner_email")} error={errors.owner_email} placeholder="you@email.com" data-testid="input-owner-email" />
                      </Field>
                      <Field label="Phone" required error={errors.owner_phone}>
                        <TextInput value={form.owner_phone} onChange={set("owner_phone")} error={errors.owner_phone} placeholder="(704) 000-0000" data-testid="input-owner-phone" />
                      </Field>
                    </div>
                    <Field label="Are you the…" required error={errors.submitter_role}>
                      <RadioPills options={ROLES} value={form.submitter_role} onChange={setVal("submitter_role")} testid="radio-role" />
                    </Field>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6" data-testid="step-property">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-forest">Property Information</h2>
                      <p className="text-ink text-sm mt-1">Share what you know — every field is optional here.</p>
                    </div>
                    <Field label="Property Address">
                      <TextInput value={form.property_address} onChange={set("property_address")} placeholder="Street address" data-testid="input-address" />
                    </Field>
                    <div className="grid sm:grid-cols-3 gap-6">
                      <Field label="City"><TextInput value={form.city} onChange={set("city")} data-testid="input-city" /></Field>
                      <Field label="County"><TextInput value={form.county} onChange={set("county")} placeholder="Union / Mecklenburg" data-testid="input-county" /></Field>
                      <Field label="State"><TextInput value={form.state} onChange={set("state")} data-testid="input-state" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Parcel ID"><TextInput value={form.parcel_id} onChange={set("parcel_id")} data-testid="input-parcel" /></Field>
                      <Field label="Approximate Acreage"><TextInput value={form.acreage} onChange={set("acreage")} placeholder="e.g. 12.5" data-testid="input-acreage" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Current Zoning"><TextInput value={form.current_zoning} onChange={set("current_zoning")} data-testid="input-zoning" /></Field>
                      <Field label="Asking Price"><TextInput value={form.asking_price} onChange={set("asking_price")} placeholder="Optional" data-testid="input-price" /></Field>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-7" data-testid="step-characteristics">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-forest">Property Characteristics</h2>
                      <p className="text-ink text-sm mt-1">A few details that help us evaluate the opportunity.</p>
                    </div>
                    <Field label="Currently Listed?">
                      <RadioPills options={["Yes", "No"]} value={form.currently_listed} onChange={setVal("currently_listed")} testid="radio-listed" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Public Water?">
                        <RadioPills options={["Available", "Not Available", "Unknown"]} value={form.water_available} onChange={setVal("water_available")} testid="radio-water" />
                      </Field>
                      <Field label="Public Sewer?">
                        <RadioPills options={["Available", "Not Available", "Unknown"]} value={form.sewer_available} onChange={setVal("sewer_available")} testid="radio-sewer" />
                      </Field>
                    </div>
                    <Field label="Approximate Road Frontage">
                      <TextInput value={form.road_frontage} onChange={set("road_frontage")} placeholder="e.g. 200 ft on Main St" data-testid="input-frontage" />
                    </Field>
                    <Field label="Existing Home or Structures?">
                      <RadioPills options={["Yes", "No"]} value={form.existing_structures} onChange={setVal("existing_structures")} testid="radio-structures" />
                    </Field>
                    <Field label="Property Type">
                      <RadioPills options={PROPERTY_TYPES} value={form.property_type} onChange={setVal("property_type")} testid="radio-property-type" />
                    </Field>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-6" data-testid="step-details">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-forest">Additional Information</h2>
                      <p className="text-ink text-sm mt-1">Tell us anything else that would help us understand the property.</p>
                    </div>
                    <Field label="Tell us about the property">
                      <TextArea rows={5} value={form.additional_info} onChange={set("additional_info")} placeholder="History, condition, access, surrounding development, reason for considering a sale…" data-testid="input-additional" />
                    </Field>
                    <div>
                      <p className="text-sm font-semibold text-forest mb-2 uppercase tracking-wider">Optional Uploads</p>
                      <p className="text-ink text-[15px] mb-4">Survey · Tax Map · Site Plan · Offering Memorandum · Property Photos · Other Documents</p>
                      <FileUpload
                        testid="property-files"
                        category="property"
                        hint="PDF, JPG, PNG, DOC — up to 25MB each"
                        value={files}
                        onChange={setFiles}
                      />
                    </div>
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-6" data-testid="step-review">
                    <div>
                      <h2 className="font-serif text-2xl font-semibold text-forest">Review &amp; Submit</h2>
                      <p className="text-ink text-sm mt-1">Please confirm your details before submitting.</p>
                    </div>
                    <Summary />
                    <div className="bg-forest/[0.03] border border-line rounded-sm p-5 flex gap-3">
                      <ShieldCheck size={22} className="text-gold shrink-0 mt-0.5" />
                      <p className="text-ink text-sm leading-relaxed">
                        Submitting a property does not obligate you to sell. Your information is kept confidential and
                        used only to evaluate the opportunity.
                      </p>
                    </div>
                    <div className="pt-1">
                      <AntiSpam onChange={setSpam} testid="property-antispam" />
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="mt-10 flex items-center justify-between gap-4 border-t border-line pt-6">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                className={`inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide px-4 py-2 transition-colors ${step === 0 ? "text-ink/30 cursor-not-allowed" : "text-forest hover:text-gold"}`}
                data-testid="form-back-btn"
              >
                <ArrowLeft size={16} /> Back
              </button>

              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} className={btn.forest} data-testid="form-next-btn">
                  Continue <ArrowRight size={18} />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={submitting} className={`${btn.gold} disabled:opacity-60`} data-testid="form-submit-btn">
                  {submitting ? "Submitting…" : "Submit Property for Review"} {!submitting && <ArrowRight size={18} />}
                </button>
              )}
            </div>
          </div>

          <p className="text-center text-ink/60 text-sm mt-6">
            Questions? Call <a href={COMPANY.phoneHref} className="text-forest font-semibold">{COMPANY.phone}</a> or email <a href={`mailto:${COMPANY.email}`} className="text-forest font-semibold">{COMPANY.email}</a>
          </p>
        </Container>
      </section>
    </div>
  );
}
